/**
 * Where a refused or broken Paya ID sign-in lands (#1951).
 *
 * Before this the callback answered with JSON, so a person whose account has no
 * OS Buddy access ended up staring at `{"code":"no_os_buddy_access"}` on the
 * API's host — a dead end with no way back and, worse, no way to try the other
 * account they may well have.
 *
 * Which is why "sign in with a different account" goes through
 * `intent=switch-account` rather than plain `/oidc/start`: single sign-on would
 * answer from the session that was just refused and drop them right back here.
 */

import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Typography } from '@mui/material';
import { AuthCard } from './AuthCard';

/**
 * The reason arrives in a query string, so it is chosen from this set and never
 * rendered: an unknown value is the generic failure, not a message an attacker
 * gets to put on the page.
 */
const REASONS = ['no-access', 'failed'] as const;
type Reason = (typeof REASONS)[number];

function reasonFrom(value: string | null): Reason {
  return REASONS.includes(value as Reason) ? (value as Reason) : 'failed';
}

export default function SignInErrorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reason = reasonFrom(searchParams.get('reason'));
  const noAccess = reason === 'no-access';

  return (
    <AuthCard>
      <Stack spacing={2}>
        <Typography variant="h6" component="h1">
          {noAccess ? t('signInError.noAccessTitle') : t('signInError.failedTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {noAccess ? t('signInError.noAccessBody') : t('signInError.failedBody')}
        </Typography>

        {/* Retrying the same session is pointless once access was refused —
            there the only move that changes the outcome is another account. */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          href={
            noAccess
              ? '/api/v1/auth/oidc/start?intent=switch-account'
              : '/api/v1/auth/oidc/start'
          }
          sx={{ mt: 1 }}
        >
          {noAccess ? t('signInError.switchAccount') : t('signInError.retry')}
        </Button>

        <Button
          variant="text"
          fullWidth
          onClick={() => void navigate('/login', { replace: true })}
        >
          {t('signInError.backToLogin')}
        </Button>
      </Stack>
    </AuthCard>
  );
}
